/*
 * Coordinate formatting / parsing utilities.
 *
 * The schedule stores RA in decimal degrees (0–360) and Dec in decimal
 * degrees (-90 to +90). Astronomers read and type sexagesimal:
 *   RA  -> HH:MM:SS.s (hours, 0–24)
 *   Dec -> ±DD:MM:SS.s
 *
 * These helpers handle both. They were extracted from the multiple inline
 * copies in UnifiedTargetStep.js, MultiTargetStep.js, QuickObservationSubmit.js,
 * etc. — single source of truth so the formatting can't drift.
 */

/**
 * True if the input string looks like sexagesimal (contains ':' or whitespace
 * between numeric components), false if it looks like a decimal degree value.
 * Empty / null strings return false.
 */
export function isSexagesimal(input) {
  if (input == null) return false;
  const s = String(input).trim();
  if (!s) return false;
  // Three numeric parts separated by ':' or whitespace
  return /[:\s]/.test(s) && s.split(/[:\s]+/).filter(Boolean).length >= 2;
}

/**
 * Parse an RA value (decimal degrees or HH:MM:SS) to decimal degrees (0–360).
 * Returns NaN on invalid input.
 */
export function parseRA(input) {
  if (input == null || input === '') return NaN;
  const s = String(input).trim();
  if (!s) return NaN;
  if (isSexagesimal(s)) {
    const parts = s.split(/[:\s]+/).filter(Boolean).map(parseFloat);
    if (parts.length < 2 || parts.some(isNaN)) return NaN;
    const [h, m = 0, sec = 0] = parts;
    return (h + m / 60 + sec / 3600) * 15;  // hours → degrees
  }
  const v = parseFloat(s);
  return isNaN(v) ? NaN : v;
}

/**
 * Parse a Dec value (decimal degrees or ±DD:MM:SS) to decimal degrees (-90 to +90).
 * Returns NaN on invalid input. Negative sign on the leading degrees component
 * is detected even when the leading number is zero (e.g., "-00:30:00").
 */
export function parseDec(input) {
  if (input == null || input === '') return NaN;
  const s = String(input).trim();
  if (!s) return NaN;
  if (isSexagesimal(s)) {
    const parts = s.split(/[:\s]+/).filter(Boolean).map(parseFloat);
    if (parts.length < 2 || parts.some(isNaN)) return NaN;
    // Sign comes from the leading character, not parseFloat — handles "-00:30:00".
    const sign = s.trimStart().startsWith('-') ? -1 : 1;
    const [d, m = 0, sec = 0] = parts.map(Math.abs);
    return sign * (d + m / 60 + sec / 3600);
  }
  const v = parseFloat(s);
  return isNaN(v) ? NaN : v;
}

/**
 * Format decimal degrees as HMS (RA convention). Returns "" for null/NaN.
 */
export function formatRAasHMS(decimal) {
  if (decimal == null || isNaN(decimal)) return '';
  // Normalize to [0, 360) before conversion.
  let d = Number(decimal) % 360;
  if (d < 0) d += 360;
  const hours = d / 15;
  const h = Math.floor(hours);
  const m = Math.floor((hours - h) * 60);
  const s = ((hours - h) * 60 - m) * 60;
  return `${pad2(h)}:${pad2(m)}:${padSec(s)}`;
}

/**
 * Format decimal degrees as ±DMS (Dec convention). Returns "" for null/NaN.
 */
export function formatDecAsDMS(decimal) {
  if (decimal == null || isNaN(decimal)) return '';
  const sign = decimal < 0 ? '-' : '+';
  const abs = Math.abs(Number(decimal));
  const d = Math.floor(abs);
  const m = Math.floor((abs - d) * 60);
  const s = ((abs - d) * 60 - m) * 60;
  return `${sign}${pad2(d)}:${pad2(m)}:${padSec(s)}`;
}

function pad2(n) { return String(Math.abs(Math.trunc(n))).padStart(2, '0'); }
function padSec(s) { return s.toFixed(2).padStart(5, '0'); }
